/*
  Night gallery — the treat card's media behaviour, on the product page.

  On the sale grid a card shows its still and swaps to the second image on hover, or once when
  it scrolls into view on touch. This gives the mystery-box product page the same behaviour
  instead of a thumbnail strip: the gallery is the reveal, so offering a row of thumbnails would
  let someone skip past the swap and study both images side by side.

  IT IS A CROSSFADE, NOT A CAROUSEL MOVE. Both frames are the same silhouette on the same
  ground, so sliding between them reads as the page moving rather than the image changing. The
  CSS in `hp-night-atmosphere` stacks the two media in one grid cell and fades the second in;
  all this file does is toggle `is-revealed` on the carousel. That is also why the second frame
  no longer peeks in beside the first on mobile: stacked, there is only ever one frame to see.

  WHY A CLASS RATHER THAN THE CAROUSEL'S OWN select().
  `main-product` is Maestrooo core and is not forked, so the markup belongs to
  `snippets/product-gallery.liquid`. `select()` scrolls, which is the slide we do not want.
  Toggling a class leaves the carousel's own scroll position, adaptive height, lazy loading and
  variant-change re-render untouched, and needs nothing from its API.

  RE-RENDER SAFETY. `product-rerender` replaces the gallery markup on variant change, so the
  carousel element this file saw at load can be thrown away at any moment. Listeners are bound
  to the SECTION, which survives, and the carousel is looked up fresh on every event.

  REDUCED MOTION: nothing moves. The visitor stays on the first image, which is the still, and
  can still swipe or scroll the gallery by hand. That mirrors the grid, where reduced motion
  means the video never plays at all.
*/

(() => {
  const MOBILE_DELAY = 1800;   // long enough to read as "then it changes", not as a glitch
  const IN_VIEW_RATIO = 0.45;

  const section = document.querySelector('.shopify-section--main-product');
  if (!section || !document.querySelector('.hpnight-host')) return;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  if (reduceMotion.matches) return;

  const carousel = () => section.querySelector('.product-gallery__carousel');

  {
    // These products carry exactly two media by contract — image 1 is the still, image 2 the
    // reveal — the same contract the sale grid cards are built on. All that is checked here is
    // that a second frame exists to fade to.
    const reveal = (on) => {
      const el = carousel();
      if (!el || el.querySelectorAll('.product-gallery__media').length < 2) return;
      el.classList.toggle('is-revealed', on);
    };

    const canHover = window.matchMedia('(hover: hover) and (pointer: fine)');

    if (canHover.matches) {
      // Delegated from the section because the carousel is replaced on variant change.
      section.addEventListener('mouseover', (event) => {
        if (event.target.closest('.product-gallery__carousel')) reveal(true);
      });

      section.addEventListener('mouseout', (event) => {
        const el = event.target.closest('.product-gallery__carousel');
        if (!el) return;
        // Moving between children of the gallery is not leaving it.
        if (event.relatedTarget && el.contains(event.relatedTarget)) return;
        reveal(false);
      });

      return;
    }

    // Touch: one pass, once, after the gallery has been on screen long enough to be looked at.
    // `played` and the disconnect mean it never fires twice and never fights a manual swipe.
    let played = false;
    let timer = null;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (played) return;

        if (!entry.isIntersecting) {
          clearTimeout(timer);
          timer = null;
          return;
        }

        if (timer) return;
        timer = setTimeout(() => {
          played = true;
          reveal(true);
          observer.disconnect();
        }, MOBILE_DELAY);
      });
    }, { threshold: IN_VIEW_RATIO });

    const el = carousel();
    if (el) observer.observe(el);
  }
})();
